import { parseStorage } from '../experiments/attribution'
import {
  browserStorageReadItem,
  browserStorageWriteItem,
} from '../experiments/browser-storage'

import {
  type UdpTrackingStoredImpression,
  udpTrackingStoredImpressionSchema,
} from './model/udpTrackingStoredImpressionSchema'
import {
  type UdpTrackingImpressionOrigin,
  type UdpTrackingProductImpression,
} from './model/udpTrackingTypes'

const IMPRESSION_STORAGE_KEY = 'udpImpression'

const impressionsByOrigin: Record<
  UdpTrackingImpressionOrigin,
  Map<string, UdpTrackingProductImpression>
> = {
  recommendation: new Map(),
  search_header: new Map(),
  search_header_popular: new Map(),
  search_page: new Map(),
}

const buildImpressionCacheKey = ({
  impressionOrigin,
  upid,
  widgetId,
}: {
  impressionOrigin: UdpTrackingImpressionOrigin
  upid: string
  widgetId?: string
}): string =>
  impressionOrigin === 'recommendation'
    ? `${encodeURIComponent(widgetId ?? '')}:${encodeURIComponent(upid)}`
    : encodeURIComponent(upid)

const buildImpressionSessionStorageKey = (
  upid: string,
  impressionOrigin: UdpTrackingImpressionOrigin,
): string =>
  `${IMPRESSION_STORAGE_KEY}:${impressionOrigin}:${encodeURIComponent(upid)}`

const getRecommendationWidgetId = (
  impression: UdpTrackingProductImpression,
): string | undefined =>
  impression.impression_origin === 'recommendation' &&
  impression.impression_origin_data &&
  'widget_id' in impression.impression_origin_data
    ? impression.impression_origin_data.widget_id
    : undefined

export const udpTrackingImpressionCacheAdd = (
  impression: UdpTrackingProductImpression,
): void => {
  const { impression_origin: impressionOrigin, product } = impression

  if (
    !impressionOrigin ||
    !impression.impression_origin_data ||
    !product.upid
  ) {
    return
  }

  const widgetId = getRecommendationWidgetId(impression)

  if (!widgetId && impressionOrigin === 'recommendation') {
    return
  }

  impressionsByOrigin[impressionOrigin].set(
    buildImpressionCacheKey({ impressionOrigin, upid: product.upid, widgetId }),
    impression,
  )
}

export const udpTrackingImpressionCacheClear = (
  impressionOrigin: UdpTrackingImpressionOrigin,
): void => {
  impressionsByOrigin[impressionOrigin].clear()
}

export const udpTrackingImpressionCacheGet = (
  upid: string,
  impressionOrigin: UdpTrackingImpressionOrigin,
  widgetId?: string,
): UdpTrackingProductImpression | undefined => {
  const impression = impressionsByOrigin[impressionOrigin].get(
    buildImpressionCacheKey({ impressionOrigin, upid, widgetId }),
  )

  return impression?.impression_origin === impressionOrigin
    ? impression
    : undefined
}

export const udpTrackingImpressionStorageGet = (
  upid: string,
  impressionOrigin: UdpTrackingImpressionOrigin,
  widgetId?: string,
): UdpTrackingStoredImpression | undefined => {
  const storedImpression = browserStorageReadItem(
    'sessionStorage',
    buildImpressionSessionStorageKey(upid, impressionOrigin),
  )

  if (!storedImpression) {
    return undefined
  }

  const result = udpTrackingStoredImpressionSchema.safeParse(
    parseStorage(storedImpression),
  )

  if (!result.success || result.data.impression_origin !== impressionOrigin) {
    return undefined
  }

  const impression = result.data

  if (
    widgetId === undefined ||
    impression.impression_origin !== 'recommendation'
  ) {
    return impression
  }

  return impression.impression_origin_data.widget_id === widgetId
    ? impression
    : undefined
}

export const udpTrackingImpressionStorageSave = ({
  attributedUpid,
  impression: impressionProp,
  impressionOrigin,
  quantity,
  upid,
  widgetId,
}: {
  attributedUpid?: string
  impression?: UdpTrackingProductImpression
  impressionOrigin: UdpTrackingImpressionOrigin
  quantity?: number
  upid?: string
  widgetId?: string
}): void => {
  if (!upid) {
    return
  }

  const impression =
    impressionProp ??
    udpTrackingImpressionCacheGet(upid, impressionOrigin, widgetId)

  if (!impression?.impression_id) {
    return
  }

  const storageUpid = attributedUpid ?? upid
  const storageKey = buildImpressionSessionStorageKey(
    storageUpid,
    impressionOrigin,
  )

  const storedImpression = udpTrackingImpressionStorageGet(
    storageUpid,
    impressionOrigin,
  )
  const storedQuantity = storedImpression?.quantity
  const nextQuantity =
    quantity === undefined ? storedQuantity : (storedQuantity ?? 0) + quantity

  const cartAdditions = storedImpression?.cart_additions ?? []

  if (quantity !== undefined && quantity > 0) {
    cartAdditions.push({
      added_at: Date.now(),
      quantity,
    })
  }

  browserStorageWriteItem(
    'sessionStorage',
    storageKey,
    JSON.stringify({
      ...impression,
      cart_additions: cartAdditions.length > 0 ? cartAdditions : undefined,
      quantity: nextQuantity,
    }),
  )
}

/** Updates the cart allocation without changing the stored click attribution. */
export const udpTrackingImpressionStorageUpdateQuantity = ({
  impressionOrigin,
  quantity,
  upid,
}: {
  impressionOrigin: UdpTrackingImpressionOrigin
  quantity?: number
  upid: string
}): void => {
  const impression = udpTrackingImpressionStorageGet(upid, impressionOrigin)

  if (!impression) {
    return
  }

  const cartAdditions = impression.cart_additions
  let removedQuantity = (impression.quantity ?? 0) - (quantity ?? 0)
  let latestAddition = cartAdditions?.at(-1)

  while (latestAddition && removedQuantity > 0) {
    const removed = Math.min(latestAddition.quantity, removedQuantity)
    latestAddition.quantity -= removed
    removedQuantity -= removed

    if (latestAddition.quantity === 0) {
      cartAdditions?.pop()
    }
    latestAddition = cartAdditions?.at(-1)
  }

  browserStorageWriteItem(
    'sessionStorage',
    buildImpressionSessionStorageKey(upid, impressionOrigin),
    JSON.stringify({
      ...impression,
      cart_additions: quantity === undefined ? undefined : cartAdditions,
      quantity,
    }),
  )
}
